import { AbstractControl } from '@angular/forms';

/**
 * Luhn mod 10, used by both Canadian identifiers (SIN and Business Number). US identifiers have no checksum.
 *
 * Rejects anything that is not purely ASCII digits rather than stripping non-digits, because a strip that
 * tolerated them would reduce the value to an empty string and report a pass.
 */
export const isValidLuhn = (value: string): boolean => {
  if (!/^\d+$/u.test(value)) {
    return false;
  }

  // Right to left: every second digit is doubled, and a doubled result over 9 has 9 subtracted.
  const sum = [...value].reverse().reduce((total, char, index) => {
    const digit = Number(char);

    if (index % 2 === 0) {
      return total + digit;
    }

    const doubled = digit * 2;

    return total + (doubled > 9 ? doubled - 9 : doubled);
  }, 0);

  return sum % 10 === 0;
};

/**
 * Validates the Luhn check digit on a Canadian identifier.
 *
 * No-ops on an empty value (`required` owns that) and on a masked value, because the API returns only a mask and
 * the form posts it straight back on an edit that leaves the field alone.
 */
export const luhnValidator = (control: AbstractControl) => {
  const value = control.value as string | undefined | null;

  if (!value || value.length < 9) {
    return null;
  }

  return isValidLuhn(value.replace(/[\s-]/gu, '')) ? null : { pattern: { message: 'Enter a valid number' } };
};
